import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Crown, Calendar, MapPin, Video, MessageCircle, Send,
  ArrowLeft, Users, Clock, User, ChevronRight, ExternalLink
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';

export default function CirclePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [circle, setCircle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchCircle();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchCircle = async () => {
    try {
      const res = await api.get('/circles/mine');
      if (!res.data) {
        navigate('/dashboard');
        return;
      }
      setCircle(res.data);
      const msgRes = await api.get(`/circles/${res.data.id}/messages`);
      setMessages(msgRes.data.messages || []);
    } catch (error) {
      console.error('Error fetching circle:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !circle) return;
    setSending(true);
    try {
      const res = await api.post(`/circles/${circle.id}/messages`, { content: newMessage });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const res = await api.post(`/circles/events/${eventId}/rsvp`);
      setCircle(prev => ({
        ...prev,
        events: prev.events.map(e =>
          e.id === eventId ? { ...e, user_rsvped: res.data.rsvped } : e
        )
      }));
      toast.success(res.data.rsvped ? "You're in!" : 'RSVP cancelled');
    } catch (error) {
      toast.error('Failed to RSVP');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-pulse text-teal">Loading your circle...</div>
      </div>
    );
  }

  if (!circle) return null;

  return (
    <div className="min-h-screen bg-cream font-body">
      <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="font-display text-xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/dashboard" className="text-sm text-text-mid hover:text-teal">Dashboard</Link>
            <span className="text-sm text-teal font-semibold">My Circle</span>
            {user?.is_admin && (
              <Link to="/admin/dashboard" className="text-xs text-text-light hover:text-teal" data-testid="admin-nav-link">Admin</Link>
            )}
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-teal hover:text-teal-deep text-sm mb-6">
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Crown className="text-teal" size={28} />
            <h1 className="font-display text-3xl text-navy">{circle.name}</h1>
          </div>
          <p className="text-text-mid flex items-center gap-2">
            <MapPin size={16} /> {circle.city} &bull; {circle.members?.length || 0} members
          </p>
        </motion.div>

        <Tabs defaultValue="members" className="space-y-6">
          <TabsList className="bg-white rounded-full p-1 border border-border-subtle">
            <TabsTrigger value="members" className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white" data-testid="circle-members-tab">
              Members
            </TabsTrigger>
            <TabsTrigger value="events" className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white" data-testid="circle-events-tab">
              Events
            </TabsTrigger>
            <TabsTrigger value="chat" className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white" data-testid="circle-chat-tab">
              Group Chat
            </TabsTrigger>
          </TabsList>

          {/* Members */}
          <TabsContent value="members">
            <div className="grid md:grid-cols-2 gap-4">
              {circle.members?.map(member => (
                <div key={member.id} className="bg-white rounded-2xl p-5 shadow-card" data-testid={`circle-member-${member.id}`}>
                  <div className="flex items-start gap-4">
                    <Avatar className="w-14 h-14">
                      <AvatarImage src={member.profile_photo_url} />
                      <AvatarFallback className="bg-seafoam/40 text-teal text-lg">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold text-navy">{member.first_name} {member.last_name?.[0]}.</p>
                      <p className="text-sm text-text-mid flex items-center gap-1">
                        <MapPin size={12} /> {member.city}
                      </p>
                      {member.bio && <p className="text-sm text-text-mid mt-2 line-clamp-3">{member.bio}</p>}
                      {member.topics_of_interest?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {member.topics_of_interest.slice(0, 4).map(topic => (
                            <span key={topic} className="text-xs bg-seafoam/50 text-teal px-2 py-0.5 rounded-full">{topic}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {member.id !== user?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/messages?to=${member.id}`)}
                        className="text-teal hover:bg-teal/10"
                        data-testid={`dm-circle-member-${member.id}`}
                      >
                        <MessageCircle size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Events */}
          <TabsContent value="events">
            <div className="space-y-6">
              {circle.events?.length > 0 && (
                <div>
                  <h3 className="font-display text-lg text-navy mb-4">Upcoming</h3>
                  <div className="space-y-3">
                    {circle.events.map(event => (
                      <div key={event.id} className="bg-white rounded-2xl p-5 shadow-card" data-testid={`circle-event-${event.id}`}>
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-teal/10 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                            <span className="text-xs text-teal font-bold">
                              {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}
                            </span>
                            <span className="text-lg font-bold text-navy">
                              {new Date(event.event_date).getDate()}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-navy">{event.title}</h4>
                              <Badge variant="outline" className={event.event_type === 'zoom' ? 'text-blue-600 border-blue-200' : 'text-green-600 border-green-200'}>
                                {event.event_type === 'zoom' ? <><Video size={10} className="mr-1" /> Zoom</> : <><MapPin size={10} className="mr-1" /> In-Person</>}
                              </Badge>
                            </div>
                            <p className="text-sm text-text-mid mt-1">
                              {new Date(event.event_date).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              {event.duration && <span> &bull; {event.duration}</span>}
                            </p>
                            {event.event_type === 'zoom' && event.zoom_link && (
                              <a href={event.zoom_link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">
                                <ExternalLink size={12} /> Join Zoom
                              </a>
                            )}
                            {event.event_type !== 'zoom' && event.location_name && (
                              <p className="text-sm text-text-mid mt-1 flex items-center gap-1">
                                <MapPin size={12} /> {event.location_name} {event.address && `- ${event.address}`}
                              </p>
                            )}
                            {event.rsvp_count > 0 && <p className="text-xs text-text-light mt-1">{event.rsvp_count} attending</p>}
                          </div>
                          <Button
                            onClick={() => handleRSVP(event.id)}
                            variant={event.user_rsvped ? 'default' : 'outline'}
                            size="sm"
                            className={`rounded-full ${event.user_rsvped ? 'bg-teal hover:bg-teal-deep text-white' : 'border-teal text-teal hover:bg-teal hover:text-white'}`}
                            data-testid={`rsvp-circle-event-${event.id}`}
                          >
                            {event.user_rsvped ? 'Going' : 'RSVP'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {circle.past_events?.length > 0 && (
                <div>
                  <h3 className="font-display text-lg text-navy mb-4">Past Events</h3>
                  <div className="space-y-3 opacity-60">
                    {circle.past_events.map(event => (
                      <div key={event.id} className="bg-white rounded-2xl p-4 shadow-card">
                        <div className="flex items-center gap-3">
                          <Clock size={16} className="text-text-light" />
                          <div>
                            <p className="font-medium text-navy">{event.title}</p>
                            <p className="text-sm text-text-light">{new Date(event.event_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!circle.events?.length && !circle.past_events?.length && (
                <div className="text-center py-12 bg-white rounded-2xl">
                  <Calendar className="mx-auto text-text-light mb-4" size={40} />
                  <p className="text-text-mid">No events scheduled yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Group Chat */}
          <TabsContent value="chat">
            <div className="bg-white rounded-2xl shadow-card overflow-hidden flex flex-col h-[500px]">
              <div className="p-4 border-b border-border-subtle">
                <h3 className="font-semibold text-navy flex items-center gap-2">
                  <MessageCircle size={18} className="text-teal" />
                  {circle.name} Chat
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-text-light py-8">
                    No messages yet. Say hello to your circle!
                  </div>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} className={`flex gap-3 ${msg.sender_id === user?.id ? 'flex-row-reverse' : ''}`}>
                      <Avatar className="w-8 h-8 flex-shrink-0">
                        <AvatarImage src={msg.sender?.profile_photo_url} />
                        <AvatarFallback className="bg-seafoam/40 text-teal text-xs">
                          {msg.sender?.first_name?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`max-w-[70%] ${msg.sender_id === user?.id ? 'text-right' : ''}`}>
                        <p className="text-xs text-text-light mb-1">{msg.sender?.first_name || 'Unknown'}</p>
                        <div className={`rounded-2xl px-4 py-2 ${msg.sender_id === user?.id ? 'bg-teal text-white rounded-tr-sm' : 'bg-cream rounded-tl-sm'}`}>
                          <p className="text-sm">{msg.content}</p>
                        </div>
                        <p className="text-xs text-text-light mt-1">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="p-4 border-t border-border-subtle">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="rounded-full border-seafoam"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    data-testid="circle-chat-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-teal hover:bg-teal-deep text-white rounded-full px-4"
                    data-testid="circle-chat-send-btn"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
